import { useState, useEffect, useContext, useCallback, useRef } from "react";
import { SocketContext } from "../pages/Chat";
import botImage from '../assets/images/hedgehog.png';
import { getMessages } from "../api/chat";

// 最後の入力から stopTyping を送るまでの時間
const TYPING_STOP_DELAY_MS = 2000;

export const useChatMain = ({
  selectedChatId,
  currentSystemUser,
  setSelectedSenderId,
  setIsLoadingMessages
}) => {
  const socket = useContext(SocketContext);
  const [messages, setMessages] = useState([]);
  // 入力中の相手ユーザーの ID（いなければ null）
  const [typingUserId, setTypingUserId] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  // Server rejected my message (e.g. blocked)
  const [sendError, setSendError] = useState(null);
  // 自分の入力中状態（送信側）
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  const typingChatIdRef = useRef(null);

  // Helper function to create match card object
  const createMatchCard = useCallback((msg) => ({
    id: msg.id,
    text: [msg.content],
    profileImageUrl: msg.suggestedUserProfileImageUrl || "/images/profileImage.png",
    currentUserId: currentSystemUser?.userId,
    currentUserName: currentSystemUser?.userName,
    currentUserImage: currentSystemUser?.profileImageUrl || "/images/profileImage.png",
    chatId: msg.chatId,
    isResponded: msg.status !== 'pending',
    apiType: msg.isSuggested ? "suggestion" : "match",
    suggestedUserName: msg.suggestedUserName || "Unknown",
    suggestedUserCategory: msg.suggestedUserCategory || "N/A",
    status: msg.status || 'pending',
    isSuggested: msg.isSuggested || false,
    relatedUserId: msg.relatedUserId,
    createdAt: msg.createdAt,
    images: msg.images || []
  }), [currentSystemUser]);

  // Helper function to create formatted message object
  const createFormattedMessage = useCallback((msg) => {
    const isBot = msg.senderId === process.env.REACT_APP_BOT_ID || 
                  msg.senderName === process.env.REACT_APP_BOT_ID;
    const isCurrentUser = currentSystemUser?.userId === msg.senderId;
    
    return {
      id: msg.id,
      sender: msg.senderName,
      senderId: msg.senderId,
      text: msg.content,
      timestamp: new Date(msg.createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }),
      rawTimestamp: msg.createdAt,
      isUser: isCurrentUser,
      profileImageUrl: isBot ? botImage : (msg.senderProfileImageUrl || "/images/profileImage.png"),
      isMatchCard: false,
      images: msg.images || [],
      readBy: msg.readBy || []
    };
  }, [currentSystemUser?.userId]);

  // Process and sort messages into match cards and regular messages
  const processMessages = useCallback((allMessages) => {
    const sortedMessages = [...allMessages].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    const matchCards = [];
    const otherMessages = [];

    sortedMessages.forEach(msg => {
      if (msg.isMatchCard) {
        matchCards.push(createMatchCard(msg));
      } else {
        otherMessages.push(createFormattedMessage(msg));
      }
    });

    return { matchCards, otherMessages };
  }, [createMatchCard, createFormattedMessage]);

  // Fetch messages from API
  const fetchMessages = useCallback(async () => {
    if (!selectedChatId) return;

    try {
      setIsLoadingMessages(true);
      const response = await getMessages(selectedChatId)
      const { matchCards, otherMessages } = processMessages(response.data);
      
      setCardsData(matchCards);
      setMessages(otherMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
      setCardsData([]);
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [selectedChatId, processMessages, setIsLoadingMessages]);

  // Handle messageRead event: update readBy in messages state
  const handleMessageRead = useCallback(({ messageId, userId }) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId && !(msg.readBy || []).includes(userId)
          ? { ...msg, readBy: [...(msg.readBy || []), userId] }
          : msg
      )
    );
  }, []);

  // Handle chatRead event: the other user opened this chat, mark my messages as read by them
  const handleChatRead = useCallback(({ chatId, userId }) => {
    if (chatId !== selectedChatId || !userId || userId === currentSystemUser?.userId) return;
    setMessages(prev =>
      prev.map(msg =>
        msg.isUser && !(msg.readBy || []).includes(userId)
          ? { ...msg, readBy: [...(msg.readBy || []), userId] }
          : msg
      )
    );
  }, [selectedChatId, currentSystemUser?.userId]);

  // Handle typing indicators
  const handleUserTyping = useCallback(({ chatId, userId }) => {
    if (chatId === selectedChatId && userId && userId !== currentSystemUser?.userId) {
      setTypingUserId(userId);
    }
  }, [selectedChatId, currentSystemUser?.userId]);

  const handleUserStoppedTyping = useCallback(({ chatId, userId }) => {
    if (chatId === selectedChatId) {
      setTypingUserId(prev => (prev && prev === userId ? null : prev));
    }
  }, [selectedChatId]);

  // Handle new incoming messages
  const handleNewMessage = useCallback((msg) => {
    if (msg.chatId !== selectedChatId) return;

    // The typing user sent a message: hide the typing indicator
    setTypingUserId(prev => (prev && prev === msg.senderId ? null : prev));

    // Mark message as read
    if (msg.id && currentSystemUser?.userId && socket) {
      socket.emit('messageRead', { 
        messageId: msg.id, 
        userId: currentSystemUser.userId 
      });
    }

    if (msg.isMatchCard) {
      const card = createMatchCard(msg);
      
      if (card.relatedUserId) {
        setSelectedSenderId(card.relatedUserId);
      }

      setCardsData(prev => {
        const exists = prev.some(existingCard => existingCard.id === card.id);
        return exists ? prev : [...prev, card];
      });
    } else {
      const formattedMessage = createFormattedMessage(msg);
      
      setMessages(prev => {
        const exists = prev.some(existingMsg => existingMsg.id === msg.id);
        if (exists) return prev;
        
        return [...prev, formattedMessage].sort(
          (a, b) => new Date(a.rawTimestamp) - new Date(b.rawTimestamp)
        );
      });
    }
  }, [
    selectedChatId, 
    currentSystemUser?.userId, 
    socket, 
    setSelectedSenderId, 
    createMatchCard, 
    createFormattedMessage
  ]);

  // Handle messageRejected event: show why my message was not sent
  const handleMessageRejected = useCallback(({ chatId, message } = {}) => {
    if (chatId !== selectedChatId) return;
    setSendError(message || 'メッセージを送信できませんでした');
  }, [selectedChatId]);

  // Stop my typing state and notify (only when I was typing)
  const stopOwnTyping = useCallback(() => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (!isTypingRef.current) return;

    const chatId = typingChatIdRef.current;
    isTypingRef.current = false;
    typingChatIdRef.current = null;

    if (socket && chatId && currentSystemUser?.userId) {
      socket.emit('stopTyping', {
        chatId,
        userId: currentSystemUser.userId
      });
    }
  }, [socket, currentSystemUser?.userId]);

  // Called on every input change: emit typing only when the state changes, stopTyping after a pause
  const handleTyping = useCallback(() => {
    if (!socket || !selectedChatId || !currentSystemUser?.userId) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      typingChatIdRef.current = selectedChatId;
      socket.emit('typing', {
        chatId: selectedChatId,
        userId: currentSystemUser.userId
      });
    }

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(stopOwnTyping, TYPING_STOP_DELAY_MS);
  }, [socket, selectedChatId, currentSystemUser?.userId, stopOwnTyping]);

  // Send message via socket
  const handleSendMessage = useCallback(async (text) => {
    if (!socket || !text?.trim() || !selectedChatId || !currentSystemUser?.userId) {
      return;
    }

    const messageData = {
      chatId: selectedChatId,
      content: text.trim(),
      senderId: currentSystemUser?.userId,
      timestamp: new Date().toISOString()
    };

    try {
      socket.emit('sendMessage', messageData);
      stopOwnTyping();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  }, [socket, selectedChatId, currentSystemUser?.userId, stopOwnTyping]);

  const handleAddMessage = useCallback((msg, onRefreshSidebar) => {
    if (msg?.type === "match_open_chat" && msg.chatId) {
      onRefreshSidebar?.();
      return;
    }
    
    if (!msg?.id) return;

    setMessages(prev => {
      const exists = prev.some(existingMsg => existingMsg.id === msg.id);
      if (exists) return prev;
      
      return [...prev, msg].sort(
        (a, b) => new Date(a.rawTimestamp) - new Date(b.rawTimestamp)
      );
    });
  }, []);

  // Effect to fetch messages when chat changes
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Clear the other user's typing indicator when the chat changes
  useEffect(() => {
    setTypingUserId(null);
    setSendError(null);
  }, [selectedChatId]);

  // On chat change / unmount, stop my typing state for the previous chat
  useEffect(() => {
    return () => {
      stopOwnTyping();
    };
  }, [selectedChatId, stopOwnTyping]);

  // Effect to handle socket events
  useEffect(() => {
    if (!socket) return;

    const events = [
      ['userTyping', handleUserTyping],
      ['stopTyping', handleUserStoppedTyping],
      ['newMessage', handleNewMessage],
      ['messageRead', handleMessageRead],
      ['chatRead', handleChatRead],
      ['messageRejected', handleMessageRejected]
    ];

    // Add event listeners
    events.forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    // Cleanup event listeners
    return () => {
      events.forEach(([event, handler]) => {
        socket.off(event, handler);
      });
    };
  }, [socket, handleUserTyping, handleUserStoppedTyping, handleNewMessage, handleMessageRead, handleChatRead, handleMessageRejected]);

  return {
    messages,
    isTyping: !!typingUserId,
    typingUserId,
    cardsData,
    socket,
    sendError,
    handleSendMessage,
    handleAddMessage,
    handleTyping
  };
};